import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';
import WhatsAppButton from '../../components/feature/WhatsAppButton';
import CookieBanner from '../../components/feature/CookieBanner';

const HomePage = () => {
  const [activeTestimonial, setActiveTestimonial] = useState(0);

  useEffect(() => {
    document.title = 'Instalación de Pladur en L\'Hospitalet de Llobregat | PLADUREX - Presupuesto Gratis';
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', 'Instalación profesional de pladur en L\'Hospitalet de Llobregat. Especialistas en tabiques, falsos techos, trasdosados y aislamiento acústico. Obra limpia, rápida y acabados perfectos. Presupuesto sin compromiso. ☎ 629 501 941');
    }
  }, []);

  const fadeInUp = {
    initial: { opacity: 0, y: 20 },
    whileInView: { opacity: 1, y: 0 },
    viewport: { once: true },
    transition: { duration: 0.6 }
  };

  const services = [
    {
      icon: 'ri-layout-grid-line',
      title: 'Tabiques de Pladur',
      items: [
        'Divisiones interiores',
        'Distribución de espacios',
        'Acabado liso para pintar',
        'Instalación rápida y limpia'
      ],
      link: '/servicios#tabiques'
    },
    {
      icon: 'ri-stack-line',
      title: 'Falsos Techos',
      items: [
        'Techos continuos',
        'Techos registrables',
        'Mejora acústica',
        'Integración de iluminación'
      ],
      link: '/servicios#techos'
    },
    {
      icon: 'ri-shield-line',
      title: 'Trasdosados y Aislamiento',
      items: [
        'Aislamiento térmico',
        'Aislamiento acústico',
        'Trasdosados directos',
        'Trasdosados autoportantes'
      ],
      link: '/servicios#trasdosados'
    },
    {
      icon: 'ri-home-gear-line',
      title: 'Reformas Integrales',
      items: [
        'Redistribución completa',
        'Viviendas y oficinas',
        'Pladur antihumedad',
        'Proyecto personalizado'
      ],
      link: '/servicios#reformas'
    }
  ];

  const benefits = [
    {
      icon: 'ri-time-line',
      title: 'Obra Rápida',
      description: 'Instalación eficiente que reduce tiempos de ejecución'
    },
    {
      icon: 'ri-paint-brush-line',
      title: 'Acabados Perfectos',
      description: 'Superficies lisas listas para pintar o decorar'
    },
    {
      icon: 'ri-volume-mute-line',
      title: 'Mejor Acústica',
      description: 'Aislamiento acústico para mayor confort'
    },
    {
      icon: 'ri-temp-cold-line',
      title: 'Eficiencia Térmica',
      description: 'Aislamiento térmico que reduce consumo energético'
    }
  ];

  const process = [
    { number: '01', title: 'Contacto', description: 'Llámanos o escríbenos por WhatsApp' },
    { number: '02', title: 'Visita', description: 'Medición y valoración in situ' },
    { number: '03', title: 'Presupuesto', description: 'Propuesta detallada sin compromiso' },
    { number: '04', title: 'Ejecución', description: 'Instalación profesional y limpia' },
    { number: '05', title: 'Entrega', description: 'Acabados perfectos listos para usar' }
  ];

  const neighborhoods = [
    'Centre', 'Santa Eulàlia', 'Collblanc-La Torrassa', 'Bellvitge', 
    'Pubilla Cases', 'Can Serra', 'La Florida', 'Gornal', 
    'Granvia Sud', 'Sanfeliu', 'Barcelona', 'Sants', 
    'Les Corts', 'Eixample', 'Cornellà', 'Esplugues'
  ];

  const faqs = [
    {
      question: '¿Cuánto tarda una instalación de pladur?',
      answer: 'Depende del proyecto. Un tabique sencillo puede estar listo en 1-2 días. Una reforma completa puede llevar entre 1-3 semanas según los metros y la complejidad.'
    },
    {
      question: '¿El pladur aísla del ruido?',
      answer: 'Sí, especialmente cuando se instala con lana mineral o lana de roca en el interior. El nivel de aislamiento acústico depende del grosor y los materiales utilizados.'
    },
    {
      question: '¿Se puede instalar pladur en baños y cocinas?',
      answer: 'Sí, utilizamos placas de pladur antihumedad (color verde) específicas para zonas húmedas como baños, cocinas y lavaderos.'
    }
  ];

  const testimonials = [
    {
      name: 'María García',
      location: 'Santa Eulàlia, L\'Hospitalet',
      text: 'Excelente trabajo en la instalación de tabiques para dividir el salón. Acabados perfectos y muy limpios durante toda la obra. Totalmente recomendables.',
      project: 'Tabiques divisorios'
    },
    {
      name: 'Carlos Martínez',
      location: 'Bellvitge, L\'Hospitalet',
      text: 'Instalaron el falso techo en mi oficina en tiempo récord. El resultado es impecable y el aislamiento acústico ha mejorado muchísimo. Muy profesionales.',
      project: 'Falso techo oficina'
    },
    {
      name: 'Ana Rodríguez',
      location: 'Collblanc, L\'Hospitalet',
      text: 'Reforma completa del baño con pladur antihumedad. Cumplieron con los plazos y el presupuesto. El equipo fue muy respetuoso trabajando en casa con nosotros.',
      project: 'Reforma baño'
    },
    {
      name: 'Jordi Puig',
      location: 'Centre, L\'Hospitalet',
      text: 'Trasdosado con aislamiento térmico en toda la vivienda. Se nota muchísimo la diferencia en la factura de calefacción. Trabajo impecable y muy buen trato.',
      project: 'Aislamiento térmico'
    }
  ];

  useEffect(() => {
    const interval = setInterval(() => {
      setActiveTestimonial((prev) => (prev + 1) % testimonials.length);
    }, 5000);
    return () => clearInterval(interval);
  }, [testimonials.length]);

  return (
    <>
      <Header />
      <WhatsAppButton />

      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center pt-32 pb-20 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-primary via-primary-dark to-primary-dark"></div>
        <div className="absolute inset-0 bg-gradient-to-b from-black/20 via-black/10 to-black/20"></div>
        
        <div className="relative z-10 w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-5 gap-8 lg:gap-12 items-center">
            {/* Contenido Izquierda */}
            <div className="lg:col-span-3">
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8 }}
              >
                <span className="inline-block px-3 sm:px-4 py-2 bg-white/10 backdrop-blur-sm text-white text-xs sm:text-sm font-medium rounded-full mb-4 sm:mb-6">
                  Instaladores profesionales en L'Hospitalet
                </span>
                <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl xl:text-7xl font-bold text-white leading-tight mb-4 sm:mb-6 break-words">
                  <strong>Instalación de pladur</strong> en L'Hospitalet de Llobregat
                </h1>
                <p className="text-base sm:text-lg lg:text-xl text-white/90 leading-relaxed mb-6 sm:mb-8 max-w-2xl">
                  Especialistas en <strong>tabiques</strong>, <strong>falsos techos</strong>, <strong>trasdosados</strong> y <strong>aislamiento acústico</strong>. Obra limpia, rápida y con acabados perfectos. Presupuesto sin compromiso.
                </p>
                <div className="flex flex-col sm:flex-row gap-3 sm:gap-4">
                  <a
                    href="tel:+34629501941"
                    className="inline-flex items-center justify-center px-6 sm:px-8 py-3 sm:py-4 bg-white text-primary font-bold text-base sm:text-lg rounded-lg hover:bg-gray-100 transition-all duration-300 shadow-lg hover:shadow-xl whitespace-nowrap cursor-pointer"
                    aria-label="Llamar a PLADUREX"
                  >
                    <i className="ri-phone-line mr-2 sm:mr-3 text-lg sm:text-xl" aria-hidden="true"></i>
                    <span className="hidden sm:inline">Llamar +34 629 501 941</span>
                    <span className="sm:hidden">Llamar Ahora</span>
                  </a>
                  <a
                    href="https://wa.me/34629501941"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center justify-center px-6 sm:px-8 py-3 sm:py-4 border-2 border-white text-white font-bold text-base sm:text-lg rounded-lg hover:bg-white hover:text-primary transition-all duration-300 whitespace-nowrap cursor-pointer"
                    aria-label="Contactar por WhatsApp"
                  >
                    <i className="ri-whatsapp-line mr-2 sm:mr-3 text-lg sm:text-xl" aria-hidden="true"></i>
                    WhatsApp
                  </a>
                </div>
              </motion.div>
            </div>

            {/* Imagen Derecha */}
            <motion.div
              className="lg:col-span-2 hidden lg:block"
              initial={{ opacity: 0, x: 30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
            >
              <div className="relative">
                <img
                  src="https://public.readdy.ai/ai/img_res/8c18ac97ab8f19a3b9ceb85dd40b58f4.jpg"
                  alt="Instalación profesional de pladur en L'Hospitalet de Llobregat - PLADUREX"
                  className="w-full h-auto rounded-3xl shadow-2xl object-cover"
                  style={{ width: '600px', height: '800px' }}
                  loading="eager"
                  width="600"
                  height="800"
                />
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Servicios Principales */}
      <section className="py-16 sm:py-20 bg-bg-light overflow-hidden">
        <div className="w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div className="text-center mb-12 sm:mb-16" {...fadeInUp}>
            <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-text-dark mb-3 sm:mb-4 px-2">
              Nuestros servicios de <strong>pladur</strong>
            </h2>
            <p className="text-base sm:text-lg lg:text-xl text-gray-600 max-w-3xl mx-auto px-4">
              <a 
                href="https://barcelona.pladures.es/" 
                target="_blank" 
                rel="noopener noreferrer nofollow"
                className="text-primary hover:text-primary-dark underline transition-colors cursor-pointer break-words"
              >
                Soluciones completas de pladur para vivienda
              </a>, oficinas y locales comerciales en L'Hospitalet de Llobregat y Barcelona
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {services.map((service, index) => (
              <motion.article
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                whileHover={{ y: -8 }}
                className="bg-white rounded-2xl p-8 shadow-lg hover:shadow-2xl transition-all duration-300 cursor-pointer"
              >
                <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mb-6">
                  <i className={`${service.icon} text-3xl text-primary`} aria-hidden="true"></i>
                </div>
                <h3 className="text-2xl font-bold text-text-dark mb-4">{service.title}</h3>
                <ul className="space-y-3 mb-6">
                  {service.items.map((item, idx) => (
                    <li key={idx} className="flex items-start text-gray-600">
                      <i className="ri-check-line text-primary mr-2 mt-1 flex-shrink-0" aria-hidden="true"></i>
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
                <Link
                  to={service.link}
                  className="inline-flex items-center text-primary font-semibold hover:text-primary-dark transition-colors group"
                  aria-label={`Ver más sobre ${service.title}`}
                >
                  Ver más
                  <i className="ri-arrow-right-line ml-2 group-hover:translate-x-1 transition-transform" aria-hidden="true"></i>
                </Link>
              </motion.article>
            ))}
          </div>
        </div>
      </section>

      {/* Beneficios */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <motion.div {...fadeInUp}>
              <img
                src="https://readdy.ai/api/search-image?query=smooth%20white%20drywall%20wall%20finish%20ready%20for%20painting%20interior%20construction%20clean%20professional%20modern%20room&width=700&height=900&seq=benefit-pladur-002&orientation=portrait"
                alt="Acabado perfecto de pladur listo para pintar - PLADUREX L'Hospitalet"
                className="w-full h-auto rounded-2xl shadow-xl object-cover"
                style={{ width: '700px', height: '900px' }}
                loading="lazy"
                width="700"
                height="900"
              />
            </motion.div>

            <motion.div {...fadeInUp}>
              <h2 className="text-4xl md:text-5xl font-bold text-text-dark mb-6">
                ¿Por qué elegir <strong>pladur</strong> para tu proyecto?
              </h2>
              <p className="text-gray-600 leading-relaxed mb-6">
                En <Link to="/empresa" className="text-primary hover:text-primary-dark font-semibold transition-colors">PLADUREX</Link> somos especialistas en instalación de pladur con años de experiencia en L'Hospitalet de Llobregat.
              </p>
              <div className="space-y-6">
                {benefits.map((benefit, index) => (
                  <div key={index} className="flex items-start">
                    <div className="w-12 h-12 bg-primary rounded-full flex items-center justify-center flex-shrink-0 mr-4">
                      <i className={`${benefit.icon} text-white text-xl`} aria-hidden="true"></i>
                    </div>
                    <div>
                      <h3 className="text-xl font-bold text-text-dark mb-2">{benefit.title}</h3>
                      <p className="text-gray-600 leading-relaxed">{benefit.description}</p>
                    </div>
                  </div>
                ))}
              </div>
              <p className="text-gray-600 leading-relaxed mt-6">
                <a 
                  href="https://grupocuartas.com/barcelona/" 
                  target="_blank" 
                  rel="noopener noreferrer nofollow"
                  className="text-primary hover:text-primary-dark underline transition-colors cursor-pointer"
                >
                  Superficies lisas listas para pintar o decorar
                </a> con acabados profesionales que garantizan resultados duraderos. Descubre todos nuestros <Link to="/servicios" className="text-primary hover:text-primary-dark font-semibold transition-colors">servicios de pladur</Link>.
              </p>
              <motion.div className="mt-8">
                <Link
                  to="/contacto"
                  className="inline-flex items-center px-8 py-4 bg-primary text-white font-bold text-lg rounded-lg hover:bg-primary-dark transition-all duration-300 shadow-lg hover:shadow-xl animate-pulse-subtle whitespace-nowrap cursor-pointer"
                  aria-label="Solicitar presupuesto de pladur"
                >
                  Solicitar Presupuesto
                  <i className="ri-arrow-right-line ml-3" aria-hidden="true"></i>
                </Link>
              </motion.div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Testimonios */}
      <section className="py-16 sm:py-20 bg-bg-light overflow-hidden">
        <div className="w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div className="text-center mb-8 sm:mb-12" {...fadeInUp}>
            <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-text-dark mb-3 sm:mb-4 px-2">
              Lo que dicen nuestros <strong>clientes</strong>
            </h2>
            <p className="text-base sm:text-lg lg:text-xl text-gray-600 max-w-3xl mx-auto px-4">
              La satisfacción de nuestros clientes es nuestra mejor carta de presentación
            </p>
          </motion.div>

          <div className="relative max-w-4xl mx-auto">
            <div className="overflow-hidden px-2 sm:px-4">
              <motion.div
                className="flex transition-transform duration-500 ease-in-out"
                style={{ transform: `translateX(-${activeTestimonial * 100}%)` }}
              >
                {testimonials.map((testimonial, index) => (
                  <div
                    key={index}
                    className="w-full flex-shrink-0 px-2 sm:px-4"
                  >
                    <article className="bg-white rounded-2xl p-6 sm:p-8 md:p-12 shadow-xl">
                      <div className="flex justify-center mb-4 sm:mb-6" aria-label="Valoración 5 estrellas">
                        {[...Array(5)].map((_, i) => (
                          <i key={i} className="ri-star-fill text-yellow-400 text-xl sm:text-2xl mx-0.5" aria-hidden="true"></i>
                        ))}
                      </div>
                      <p className="text-base sm:text-lg md:text-xl lg:text-2xl text-gray-700 text-center leading-relaxed mb-6 sm:mb-8 italic px-2">
                        "{testimonial.text}"
                      </p>
                      <div className="text-center">
                        <div className="w-14 h-14 sm:w-16 sm:h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-3 sm:mb-4">
                          <i className="ri-user-line text-2xl sm:text-3xl text-primary" aria-hidden="true"></i>
                        </div>
                        <h3 className="text-lg sm:text-xl font-bold text-text-dark">{testimonial.name}</h3>
                        <p className="text-sm sm:text-base text-gray-600">{testimonial.location}</p>
                        <span className="inline-block mt-2 px-3 sm:px-4 py-1 bg-primary/10 text-primary text-xs sm:text-sm font-semibold rounded-full">
                          {testimonial.project}
                        </span>
                      </div>
                    </article>
                  </div>
                ))}
              </motion.div>
            </div>

            {/* Navegación del carrusel */}
            <div className="flex justify-center items-center mt-6 sm:mt-8 gap-2 sm:gap-3">
              {testimonials.map((_, index) => (
                <button
                  key={index}
                  onClick={() => setActiveTestimonial(index)}
                  className={`w-2.5 h-2.5 sm:w-3 sm:h-3 rounded-full transition-all duration-300 cursor-pointer ${
                    activeTestimonial === index
                      ? 'bg-primary w-6 sm:w-8'
                      : 'bg-gray-300 hover:bg-gray-400'
                  }`}
                  aria-label={`Ver testimonio ${index + 1}`}
                />
              ))}
            </div>

            {/* Flechas de navegación - Solo en desktop */}
            <button
              onClick={() => setActiveTestimonial((prev) => (prev - 1 + testimonials.length) % testimonials.length)}
              className="hidden md:flex absolute left-0 top-1/2 -translate-y-1/2 -translate-x-4 lg:-translate-x-12 w-10 h-10 lg:w-12 lg:h-12 bg-white rounded-full shadow-lg flex items-center justify-center hover:bg-gray-50 transition-colors cursor-pointer"
              aria-label="Testimonio anterior"
            >
              <i className="ri-arrow-left-s-line text-xl lg:text-2xl text-gray-700" aria-hidden="true"></i>
            </button>
            <button
              onClick={() => setActiveTestimonial((prev) => (prev + 1) % testimonials.length)}
              className="hidden md:flex absolute right-0 top-1/2 -translate-y-1/2 translate-x-4 lg:translate-x-12 w-10 h-10 lg:w-12 lg:h-12 bg-white rounded-full shadow-lg flex items-center justify-center hover:bg-gray-50 transition-colors cursor-pointer"
              aria-label="Testimonio siguiente"
            >
              <i className="ri-arrow-right-s-line text-xl lg:text-2xl text-gray-700" aria-hidden="true"></i>
            </button>
          </div>
        </div>
      </section>

      {/* Proceso de Trabajo */}
      <section className="py-16 sm:py-20 bg-bg-light overflow-hidden">
        <div className="w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div className="text-center mb-12 sm:mb-16" {...fadeInUp}>
            <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-text-dark mb-3 sm:mb-4 px-2">
              Cómo trabajamos
            </h2>
            <p className="text-base sm:text-lg lg:text-xl text-gray-600 max-w-3xl mx-auto px-4">
              Proceso simple y transparente desde el primer contacto hasta la entrega final. <Link to="/empresa" className="text-primary hover:text-primary-dark font-semibold transition-colors">Conoce más sobre nuestra empresa</Link>.
            </p>
          </motion.div>

          <div className="relative">
            {/* Línea conectora */}
            <div className="hidden lg:block absolute top-12 left-0 right-0 h-1 bg-primary/20" style={{ width: '80%', margin: '0 auto' }}></div>

            <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-5 gap-8">
              {process.map((step, index) => (
                <motion.article
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.6, delay: index * 0.1 }}
                  className="relative text-center"
                >
                  <div className="w-24 h-24 bg-primary rounded-full flex items-center justify-center mx-auto mb-4 shadow-lg relative z-10">
                    <span className="text-3xl font-bold text-white">{step.number}</span>
                  </div>
                  <h3 className="text-xl font-bold text-text-dark mb-2">{step.title}</h3>
                  <p className="text-gray-600">{step.description}</p>
                </motion.article>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Zonas de Servicio */}
      <section className="py-16 sm:py-20 bg-white overflow-hidden">
        <div className="w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div className="text-center mb-8 sm:mb-12" {...fadeInUp}>
            <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-text-dark mb-3 sm:mb-4 px-2">
              Zonas de actuación
            </h2>
            <p className="text-base sm:text-lg lg:text-xl text-gray-600 max-w-3xl mx-auto px-4">
              <strong>Instalación de pladur en L'Hospitalet de Llobregat</strong>, Barcelona y alrededores. <Link to="/contacto" className="text-primary hover:text-primary-dark font-semibold transition-colors">Solicita presupuesto</Link> para tu zona.
            </p>
          </motion.div>

          <motion.div 
            className="flex flex-wrap justify-center gap-2 sm:gap-3"
            {...fadeInUp}
          >
            {neighborhoods.map((neighborhood, index) => (
              <span
                key={index}
                className="px-4 sm:px-6 py-2 sm:py-3 bg-primary/10 text-primary font-semibold text-sm sm:text-base rounded-full hover:bg-primary hover:text-white transition-all duration-300 cursor-pointer"
              >
                {neighborhood}
              </span>
            ))}
          </motion.div>
        </div>
      </section>

      {/* Mini FAQ */}
      <section className="py-16 sm:py-20 bg-bg-light overflow-hidden">
        <div className="w-full max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div className="text-center mb-8 sm:mb-12" {...fadeInUp}>
            <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-text-dark mb-3 sm:mb-4 px-2">
              Preguntas frecuentes
            </h2>
          </motion.div>

          <div className="space-y-4">
            {faqs.map((faq, index) => (
              <motion.article
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                className="bg-white rounded-xl p-6 shadow-md"
              >
                <h3 className="text-xl font-bold text-text-dark mb-3 flex items-start">
                  <i className="ri-question-line text-primary mr-3 sm:mr-3 mt-1" aria-hidden="true"></i>
                  {faq.question}
                </h3>
                <p className="text-gray-600 leading-relaxed pl-9">{faq.answer}</p>
              </motion.article>
            ))}
          </div>

          <motion.div className="text-center mt-8" {...fadeInUp}>
            <Link
              to="/faq"
              className="inline-flex items-center text-primary font-semibold text-lg hover:text-primary-dark transition-colors group"
              aria-label="Ver todas las preguntas frecuentes sobre pladur"
            >
              Ver todas las preguntas frecuentes
              <i className="ri-arrow-right-line ml-2 group-hover:translate-x-1 transition-transform" aria-hidden="true"></i>
            </Link>
          </motion.div>
        </div>
      </section>

      {/* CTA Final */}
      <section className="py-16 sm:py-20 bg-primary-dark relative overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0" style={{ backgroundImage: 'repeating-linear-gradient(45deg, transparent, transparent 35px, rgba(255,255,255,.1) 35px, rgba(255,255,255,.1) 70px)' }}></div>
        </div>
        
        <div className="relative z-10 w-full max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div {...fadeInUp}>
            <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-white mb-4 sm:mb-6 px-2">
              ¿Listo para tu proyecto de pladur?
            </h2>
            <p className="text-base sm:text-lg lg:text-xl text-white/90 mb-6 sm:mb-8 leading-relaxed px-4">
              Contacta con nosotros y recibe un <strong>presupuesto personalizado sin compromiso</strong>. Atendemos en L'Hospitalet de Llobregat, Barcelona y alrededores.
            </p>
            <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 justify-center px-2">
              <a
                href="tel:+34629501941"
                className="inline-flex items-center justify-center px-6 sm:px-8 py-3 sm:py-4 bg-white text-primary font-bold text-base sm:text-lg rounded-lg hover:bg-gray-100 transition-all duration-300 shadow-lg hover:shadow-xl whitespace-nowrap cursor-pointer"
                aria-label="Llamar a PLADUREX al 629 501 941"
              >
                <i className="ri-phone-line mr-2 sm:mr-3 text-lg sm:text-xl" aria-hidden="true"></i>
                <span className="hidden sm:inline">Llamar +34 629 501 941</span>
                <span className="sm:hidden">Llamar Ahora</span>
              </a>
              <a
                href="https://wa.me/34629501941"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center justify-center px-6 sm:px-8 py-3 sm:py-4 bg-[#25D366] text-white font-bold text-base sm:text-lg rounded-lg hover:bg-[#20BA5A] transition-all duration-300 shadow-lg hover:shadow-xl whitespace-nowrap cursor-pointer"
                aria-label="Contactar por WhatsApp"
              >
                <i className="ri-whatsapp-line mr-2 sm:mr-3 text-lg sm:text-xl" aria-hidden="true"></i>
                WhatsApp
              </a>
            </div>
          </motion.div>
        </div>
      </section>

      <Footer />

      <CookieBanner />

      {/* JSON-LD Schema - LocalBusiness */}
      <script type="application/ld+json">
        {JSON.stringify({
          "@context": "https://schema.org",
          "@type": "LocalBusiness",
          "name": "PLADUREX - Instalación de Pladur en L'Hospitalet",
          "image": `${import.meta.env.VITE_SITE_URL || ''}/og-image.jpg`,
          "telephone": "+34629501941",
          "url": import.meta.env.VITE_SITE_URL || '',
          "address": {
            "@type": "PostalAddress",
            "addressLocality": "L'Hospitalet de Llobregat",
            "addressRegion": "Barcelona",
            "addressCountry": "ES"
          },
          "geo": {
            "@type": "GeoCoordinates",
            "latitude": "41.359722",
            "longitude": "2.100556"
          },
          "areaServed": [
            {
              "@type": "City",
              "name": "L'Hospitalet de Llobregat"
            },
            {
              "@type": "City",
              "name": "Barcelona"
            }
          ],
          "priceRange": "€€",
          "openingHours": "Mo-Fr 08:00-19:00",
          "aggregateRating": {
            "@type": "AggregateRating",
            "ratingValue": "5",
            "reviewCount": "4"
          }
        })}
      </script>

      {/* JSON-LD Schema - Service */}
      <script type="application/ld+json">
        {JSON.stringify({
          "@context": "https://schema.org",
          "@type": "Service",
          "serviceType": "Instalación de Pladur",
          "provider": {
            "@type": "LocalBusiness",
            "name": "PLADUREX"
          },
          "areaServed": {
            "@type": "City",
            "name": "L'Hospitalet de Llobregat"
          },
          "hasOfferCatalog": {
            "@type": "OfferCatalog",
            "name": "Servicios de Pladur",
            "itemListElement": [
              {
                "@type": "Offer",
                "itemOffered": {
                  "@type": "Service",
                  "name": "Tabiques de Pladur"
                }
              },
              {
                "@type": "Offer",
                "itemOffered": {
                  "@type": "Service",
                  "name": "Falsos Techos"
                }
              },
              {
                "@type": "Offer",
                "itemOffered": {
                  "@type": "Service",
                  "name": "Trasdosados y Aislamiento"
                }
              },
              {
                "@type": "Offer",
                "itemOffered": {
                  "@type": "Service",
                  "name": "Reformas Integrales"
                }
              }
            ]
          }
        })}
      </script>
    </>
  );
};

export default HomePage;
